/**
 * CancelWorkshopFlow — everything after "Continue to rebooking": the full-screen
 * picker the booking page's rebook uses, a blocked notice while sales are still
 * settling, and the confirm step with its required reason. A workshop with no
 * confirmed bookings skips the picker and goes straight to the confirm step.
 */
import type { WorkshopDetail } from '../../../../api/workshops';
import { RebookWorkshopPicker } from '../../../bookings/shared/RebookWorkshopPicker';

import { CancelBlockedDialog } from './CancelBlockedDialog';
import { CancelConfirmDialog } from './CancelConfirmDialog';
import type { CancelledBannerDetail } from './CancelledBanner';
import { CancelProcessingModal } from './CancelProcessingModal';
import { pickedLabel, useCancelWorkshopFlow } from './use-cancel-workshop-flow';

export interface CancelWorkshopFlowProps {
  workshop: WorkshopDetail;
  onClose: () => void;
  /** Called once the cancel committed; null detail when nobody was moved. */
  onCancelled: (detail: CancelledBannerDetail | null) => void;
}

export function CancelWorkshopFlow({ workshop, onClose, onCancelled }: CancelWorkshopFlowProps) {
  const flow = useCancelWorkshopFlow(workshop, onCancelled);
  const { targets, attendees } = flow;

  if (flow.blocked) return <CancelBlockedDialog pending={flow.blocked} onClose={onClose} />;

  if (flow.picked || flow.nothingToMove) {
    return (
      <>
        <CancelConfirmDialog
          bookings={flow.bookings}
          attendees={attendees}
          targetLabel={flow.picked ? pickedLabel(flow.picked) : null}
          errorMessage={flow.errorMessage}
          processing={flow.processing}
          onBack={flow.nothingToMove ? onClose : () => flow.pick(null)}
          onConfirm={(reason) => void flow.confirm(reason)}
        />
        {flow.processing && <CancelProcessingModal />}
      </>
    );
  }

  const typeName = workshop.workshopType.displayName ?? workshop.workshopType.name;
  return (
    <RebookWorkshopPicker
      workshops={targets.workshops}
      loading={targets.loading}
      error={targets.error}
      truncated={targets.envelope?.truncated ?? false}
      regionName={workshop.venueRegionName}
      sourceLabel={`${typeName} · ${workshop.venueName}`}
      headerSubtitle={
        <>
          Moving all {attendees} booked attendee{attendees === 1 ? '' : 's'} off this workshop. Same
          price, same region.
        </>
      }
      treeOptions={targets.treeOptions}
      treeSpeciesId={targets.treeSpeciesId}
      onTreeChange={targets.setTreeSpeciesId}
      viewMode={flow.viewMode}
      onViewModeChange={flow.setViewMode}
      month={flow.month}
      onMonthChange={flow.setMonth}
      onBack={onClose}
      onClose={onClose}
      onPick={flow.pick}
    />
  );
}
